import React, { useState, useEffect, useMemo } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import { Provider } from "react-redux";
import { Modal } from "antd";
import { jwtDecode } from "jwt-decode";
import { store } from "./Utilities/store.js";
import DeviceInfo from "./Config/DeviceInfo";
import DynamicHeader from "./Config/DynamicHeader.js";

// Page
import Home from "./Pages/Home/Home";
import About from "./Pages/About/About";
import Courses from "./Pages/Courses/Courses";
import Class from "./Pages/Class/Class";
import PreviewClass from "./Pages/PreviewClass/PreviewClass";
import Session from "./Pages/Session/Session.js";
import Signup from "./Pages/SignUp/SignUp";
import SignIn from "./Pages/SignIn/SignIn";
import Reset from "./Pages/ResetPage/Reset";
import VerifyOTP from "./Pages/ResetPage/verifyOTP/VerifyOTP";
import VerifySignUpOTP from "./Pages/ResetPage/verifySignUpOTP/verifySignUpOTP";
import ChangePassword from "./Pages/ResetPage/ChangePassword/ChangePassword";
import LoginFlag from "./Pages/LoginFlag/LoginFlag.js";
import FAQ from "./Pages/FAQ/FAQ.js";
import UserProfile from "./Pages/UserProfile/UserProfile.js";
import UserClasses from "./Pages/UserClasses/UserClasses.js";
import AccountSetting from "./Pages/AccountSetting/AccountSetting.js";
import Cart from "./Pages/Cart/Cart.js";
import TermsOfUse from "./Pages/TermsOfUse/TermsOfUse.js";
import TeachingLanding from "./Pages/TeachingLanding/TeachingLanding.js";
import TeacherHome from "./Pages/TeacherHome/TeacherHome.js";
import CreateClass from "./Pages/CreateClass/CreateClass.js";
import EditClass from "./Pages/EditClass/EditClass.js";
import MainContent from "./Aministrator/AdminMainContent/AdminMainContent.js";
import PaymentStatus from "./Components/PaymentStatus/PaymentStatus.js";
import AwaitPaymentStatus from "./Components/AwaitPaymentStatus/AwaitPaymentStatus.js";
import WhatsAppSupport from "./Components/WhatsAppSupport/WhatsAppSupport.js";
import KYCVerification from "./Pages/KYCVerification/KYCVerification.js";

const AppWithNavigation = ({ isLoggedIn, setLoggedIn, deviceId, userIp, profileImage, setProfileImage }) => {

const location = useLocation();
const hideHeaderRoutes = useMemo(() => [], []);

return (
  <>
    {!hideHeaderRoutes.includes(location.pathname) && (
      <DynamicHeader isLoggedIn={isLoggedIn} setLoggedIn={setLoggedIn} profileImage={profileImage} setProfileImage={setProfileImage} />
    )}

    <Routes>
      <Route path="/" element={<Home isLoggedIn={isLoggedIn} />} />
      <Route path="/about-us" element={<About />} />
      <Route path="/courses" element={<Courses />} />
      <Route path="/class/:classId" element={<Class />} />
      <Route path="/preview-class/:classId" element={<PreviewClass />} />
      <Route path="/session" element={<Session />} />
      <Route path="/signup" element={<Signup setLoggedIn={setLoggedIn} deviceId={deviceId} userIp={userIp} />} />
      <Route path="/signin" element={<SignIn setLoggedIn={setLoggedIn} deviceId={deviceId} userIp={userIp}  setProfileImage={setProfileImage}/>} />
      <Route path="/reset" element={<Reset setLoggedIn={setLoggedIn} deviceId={deviceId} userIp={userIp} />} />
      <Route path="/verify-otp" element={<VerifyOTP setLoggedIn={setLoggedIn} deviceId={deviceId} userIp={userIp} />} />
      <Route path="/verify-signup-otp" element={<VerifySignUpOTP setLoggedIn={setLoggedIn} deviceId={deviceId} userIp={userIp} />} />
      <Route path="/change-password" element={<ChangePassword setLoggedIn={setLoggedIn} deviceId={deviceId} userIp={userIp} />} />
      <Route path="/login-flag" element={<LoginFlag setLoggedIn={setLoggedIn} deviceId={deviceId} userIp={userIp} />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/user-profile" element={<UserProfile setProfileImage={setProfileImage} />} />
      <Route path="/user-classes" element={<UserClasses />} />
      <Route path="/account-settings" element={<AccountSetting />} />      
      <Route path="/cart" element={<Cart />} />
      <Route path="/terms-of-service" element={<TermsOfUse />} />
      <Route path="/become-a-teacher" element={<TeachingLanding />} />
      <Route path="/payment-status" element={<PaymentStatus />} />
      <Route path="/await-payment=status" element={<AwaitPaymentStatus />} />
      <Route path="/teacher-home" element={<TeacherHome />} />
      <Route path="/teacher-createClass" element={<CreateClass deviceId={deviceId} userIp={userIp} />} />
      <Route path="/teacher-createClass/:classId" element={<EditClass deviceId={deviceId} userIp={userIp} />} />
      <Route path="/admin-page/*" element={<MainContent />} />
      <Route path="/kyc-page" element={<KYCVerification />} />
    </Routes>
  </>
);
};

const App = () => {
  const [isLoggedIn, setLoggedIn] = useState(false);
  const [deviceId, setDeviceId] = useState("");
  const [userIp, setUserIp] = useState("Fetching...");
  const [isSessionExpired, setIsSessionExpired] = useState(false);
  const [profileImage, setProfileImage] = useState(null);

  const handleSessionExpired = () => {
    setIsSessionExpired(true);
    localStorage.removeItem("accessToken");
    localStorage.removeItem("userInfo");
    setLoggedIn(false);
  };

useEffect(() => {
  if (isLoggedIn) {
    const photo = localStorage.getItem("userPhoto");
    setProfileImage(photo || null);
  }
}, [isLoggedIn]);

  const checkTokenExpiration = () => {
    const token = localStorage.getItem("accessToken");
    if (token) {
      try {
        const decoded = jwtDecode(token);
        const currentTime = Date.now() / 1000;
        if (decoded.exp < currentTime) {
          handleSessionExpired();
        }
      } catch (err) {
        console.error("Invalid token:", err);
      }
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (token) {
      setLoggedIn(true);
      checkTokenExpiration();
    }

    const interval = setInterval(checkTokenExpiration, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleSessionExpiredClose = () => {
    setIsSessionExpired(false);
    window.location.href = "/signin";
  };

  return (
    <Provider store={store}>
      <Router>
        <DeviceInfo setDeviceId={setDeviceId} setUserIp={setUserIp} />

        <Modal
          title="Session Expired"
          open={isSessionExpired}
          onOk={handleSessionExpiredClose}
          onCancel={handleSessionExpiredClose}
          closable={false}
          maskClosable={false}
          cancelButtonProps={{ style: { display: "none" } }}
        >
          <p>Your session has expired. Please log in again.</p>
        </Modal>

        <AppWithNavigation
          isLoggedIn={isLoggedIn}
          setLoggedIn={setLoggedIn}
          deviceId={deviceId}
          userIp={userIp}
          profileImage={profileImage}
          setProfileImage={setProfileImage}
        />
          <WhatsAppSupport />
      </Router>
    </Provider>
  );
};

export default App;